import { useEffect, useRef } from "react";

export function HeroCanvas({ heroRef }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const hero = heroRef.current;
    if (!canvas || !hero) return undefined;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const context = canvas.getContext("2d");
    let particles = [];
    let pointer = { x: -9999, y: -9999 };
    let frame;

    const resize = () => {
      const rect = hero.getBoundingClientRect();
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(1, Math.floor(rect.width * ratio));
      canvas.height = Math.max(1, Math.floor(rect.height * ratio));
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      const count = window.innerWidth < 760 ? 14 : 28;
      particles = Array.from({ length: count }, () => ({
        x: Math.random() * rect.width,
        y: Math.random() * rect.height,
        vx: (Math.random() - 0.5) * 0.12,
        vy: (Math.random() - 0.5) * 0.12,
        r: Math.random() * 1.5 + 0.7,
      }));
    };

    const draw = () => {
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      context.clearRect(0, 0, width, height);
      particles.forEach((p, index) => {
        if (!reduceMotion) {
          p.x += p.vx;
          p.y += p.vy;
          if (p.x < 0 || p.x > width) p.vx *= -1;
          if (p.y < 0 || p.y > height) p.vy *= -1;
        }
        const pointerDistance = Math.hypot(p.x - pointer.x, p.y - pointer.y);
        const near = pointerDistance < 170;
        context.beginPath();
        context.arc(p.x, p.y, p.r + (near ? 1 : 0), 0, Math.PI * 2);
        context.fillStyle = `rgba(242,210,143,${near ? 0.85 : 0.42})`;
        context.fill();
        for (let j = index + 1; j < particles.length; j += 1) {
          const other = particles[j];
          const distance = Math.hypot(p.x - other.x, p.y - other.y);
          if (distance < 145) {
            context.beginPath();
            context.moveTo(p.x, p.y);
            context.lineTo(other.x, other.y);
            context.strokeStyle = `rgba(242,210,143,${(1 - distance / 145) * 0.18})`;
            context.lineWidth = 0.7;
            context.stroke();
          }
        }
      });
      if (!reduceMotion) frame = requestAnimationFrame(draw);
    };

    const onMove = (event) => {
      const rect = hero.getBoundingClientRect();
      pointer = { x: event.clientX - rect.left, y: event.clientY - rect.top };
    };
    const onLeave = () => {
      pointer = { x: -9999, y: -9999 };
    };

    hero.addEventListener("pointermove", onMove);
    hero.addEventListener("pointerleave", onLeave);
    window.addEventListener("resize", resize, { passive: true });
    resize();
    draw();
    return () => {
      cancelAnimationFrame(frame);
      hero.removeEventListener("pointermove", onMove);
      hero.removeEventListener("pointerleave", onLeave);
      window.removeEventListener("resize", resize);
    };
  }, [heroRef]);

  return <canvas ref={canvasRef} className="legacy-canvas" aria-hidden="true" />;
}
